import { useEffect, useRef, useState, memo } from "react";
import { ImageOff } from "lucide-react";

interface ChatImageBubbleProps {
  encryptedData: string;
  decryptBinary: (s: string) => Promise<Uint8Array | null>;
  onClick: (objectUrl: string) => void;
}

export const ChatImageBubble = memo(function ChatImageBubble({
  encryptedData,
  decryptBinary,
  onClick,
}: ChatImageBubbleProps) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const decrypt = async () => {
      try {
        const bytes = await decryptBinary(encryptedData);
        if (cancelled) return;
        if (!bytes) {
          setFailed(true);
          return;
        }
        const blob = new Blob([bytes.buffer as ArrayBuffer], { type: "image/jpeg" });
        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        setObjectUrl(url);
      } catch {
        if (!cancelled) setFailed(true);
      }
    };

    decrypt();

    return () => {
      cancelled = true;
      // Revoke object URL on unmount to prevent memory leaks and browser caching
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  // decryptBinary is stable (from useCallback), encryptedData is the key
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encryptedData]);

  if (failed) {
    return (
      <div className="w-[180px] h-[180px] rounded-xl flex flex-col items-center justify-center bg-app-surface-raised border border-app-border/30 gap-2">
        <ImageOff className="w-6 h-6 text-app-text-muted" />
        <span className="text-[11px] text-app-text-muted">Decryption failed</span>
      </div>
    );
  }

  if (!objectUrl) {
    // Shimmer skeleton while decrypting
    return (
      <div
        className="w-[180px] h-[180px] rounded-xl overflow-hidden"
        style={{
          background: "linear-gradient(90deg, rgba(255,255,255,0.06) 25%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.06) 75%)",
          backgroundSize: "200% 100%",
          animation: "shimmer 1.4s infinite linear",
        }}
      >
        <style>{`
          @keyframes shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onClick(objectUrl)}
      className="block w-[180px] h-[180px] rounded-xl overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-app-primary active:scale-95 transition-transform"
    >
      <img
        src={objectUrl}
        alt="Encrypted photo"
        className="w-full h-full object-cover"
        draggable={false}
      />
    </button>
  );
});
